import { Tab } from "@headlessui/react"
import React, {
  ReactNode,
  useState,
  useRef,
  useEffect,
  useCallback,
} from "react"
import { Image } from "~/ui-rtk/components/ui/common/Image"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline"
import { throttle } from "lodash"
import "./style.scss"

export type TTabsGroupOption = {
  label: string
  icon?: {
    src: string
    alt?: string
  }
  disabled?: boolean
}

type TTabsGroupProps = {
  options: TTabsGroupOption[]
  index?: number
  onChange: (value: number) => void
  children?: (option: TTabsGroupOption, idx: number) => ReactNode | null
}

export default function TabsGroup({
  options,
  index: indexProps,
  onChange: onChangeFromProps,
  children,
}: TTabsGroupProps) {
  const isControlled = typeof indexProps !== "undefined"
  const [internalIndex, setInternalIndex] = useState<number>(indexProps ?? 0)
  const activeIndex = isControlled ? indexProps : internalIndex
  const [showNavigation, setShowNavigation] = useState(false)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const tabsRef = useRef<(HTMLElement | null)[]>([])

  const onChange = (e: number) => {
    if (onChangeFromProps) {
      onChangeFromProps(e)
    }

    if (!isControlled) {
      setInternalIndex(e)
    }
  }

  const scroll = (direction: "left" | "right") => {
    if (!containerRef?.current) return

    const container = containerRef.current
    const tabList = container?.querySelector?.('[role="tablist"]')
    if (!tabList) return

    const tabElements = Array.from(tabList?.children || [])
    const containerRect = container?.getBoundingClientRect()
    const tabRects = tabElements.map(tab => tab?.getBoundingClientRect())

    if (direction === "left") {
      // Find the first visible tab
      const firstVisibleIndex = tabRects.findIndex(
        rect => rect?.right > containerRect?.left,
      )

      if (firstVisibleIndex > 0) {
        // Get the previous tab's position
        const previousRect = tabRects[firstVisibleIndex - 1]

        // Scroll to align the previous tab with the right edge
        container?.scrollTo?.({
          left:
            (container?.scrollLeft || 0) -
            ((containerRect?.right || 0) - (previousRect?.right || 0)),
          behavior: "smooth",
        })
      }
    } else {
      // Find the last fully visible tab
      const lastVisibleIndex =
        tabRects.findIndex(rect => rect?.left >= containerRect?.right) - 1

      if (lastVisibleIndex >= 0 && lastVisibleIndex < tabElements.length - 1) {
        // Get the next tab's position
        const nextRect = tabRects[lastVisibleIndex + 1]

        // Scroll to align the next tab with the left edge
        container?.scrollTo?.({
          left:
            (container?.scrollLeft || 0) +
            ((nextRect?.left || 0) - (containerRect?.left || 0)),
          behavior: "smooth",
        })
      }
    }
  }

  const checkScrollability = useCallback(() => {
    if (containerRef.current) {
      const container = containerRef.current
      const tabList = container?.querySelector?.('[role="tablist"]')
      if (!tabList) return

      const tabElements = Array.from(tabList?.children || [])
      const containerRect = container?.getBoundingClientRect()
      const tabRects = tabElements.map(tab => tab?.getBoundingClientRect())

      // Find the first and last visible tabs
      const firstVisibleIndex = tabRects.findIndex(
        rect => rect?.right > containerRect?.left,
      )

      const lastVisibleIndex =
        tabRects.findIndex(rect => rect?.left >= containerRect?.right) - 1

      setCanScrollLeft(firstVisibleIndex > 0)
      setCanScrollRight(
        lastVisibleIndex >= 0 && lastVisibleIndex < tabElements.length - 1,
      )
      setShowNavigation(
        tabElements.length > 0 &&
          containerRect?.width < (tabList?.scrollWidth || 0),
      )
    }
  }, [])

  const throttledCheckScrollability = useCallback(
    throttle(checkScrollability, 150, { leading: true, trailing: true }),
    [checkScrollability],
  )

  useEffect(() => {
    checkScrollability()
    window.addEventListener("resize", throttledCheckScrollability)
    return () => {
      window.removeEventListener("resize", throttledCheckScrollability)
      throttledCheckScrollability.cancel()
    }
  }, [options, throttledCheckScrollability])

  return (
    <div className="relative">
      {showNavigation && (
        <>
          {canScrollLeft && (
            <button
              onClick={() => scroll("left")}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-system-xs rounded-full bg-system-blue-800 hover:bg-system-blue-700 text-white"
              aria-label="Scroll left"
            >
              <ChevronLeftIcon className="w-system-md h-system-md" />
            </button>
          )}
          {canScrollRight && (
            <button
              onClick={() => scroll("right")}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-system-xs rounded-full bg-system-blue-800 hover:bg-system-blue-700 text-white"
              aria-label="Scroll right"
            >
              <ChevronRightIcon className="w-system-md h-system-md" />
            </button>
          )}
        </>
      )}
      <div className="relative">
        <div
          ref={containerRef}
          className="relative flex justify-start items-center border-b border-system-blue-600 tabs-group-scroll"
          onScroll={throttledCheckScrollability}
        >
          <Tab.Group selectedIndex={activeIndex} onChange={onChange}>
            <Tab.List className="flex gap-system-xs min-w-full">
              {options.map((tab, idx) => (
                <Tab
                  ref={el => (tabsRef.current[idx] = el)}
                  disabled={tab.disabled}
                  key={idx}
                  className="focus-visible:outline-none shrink-0"
                >
                  <div
                    className={cn(
                      "flex items-center border border-system-blue-600 border-b-0 text-base text-system-gray-300 px-system-md py-system-sm font-medium font-lato rounded-none rounded-t-system-md",
                      {
                        "bg-system-blue-600 text-white font-semibold":
                          activeIndex === idx,
                        "bg-system-blue-800 border-system-blue-700":
                          activeIndex !== idx,
                      },
                    )}
                  >
                    {children ? (
                      children(tab, idx)
                    ) : (
                      <React.Fragment>
                        {tab.icon ? (
                          <Image
                            src={tab.icon.src}
                            alt={tab.icon.alt ?? tab.label}
                            classes={{
                              wrapper: "me-system-xs",
                            }}
                          />
                        ) : null}
                        {tab.label}
                      </React.Fragment>
                    )}
                  </div>
                </Tab>
              ))}
            </Tab.List>
          </Tab.Group>
        </div>
      </div>
    </div>
  )
}
